// @ts-strict-ignore
import * as React from 'react';
import { RootState } from '../../reducers/reducers';
import {
    appleAppCanBeDeveloperRejected,
    appleAppIsApprovedOrReleased,
    appStoreTitleForState,
    getAutomationIdByType,
    getIsAppleAppStore,
    getIsGooglePlayStore,
    getIsHuaweiAppGallery,
    getIsSamsungGalaxyStore,
    googleAppIsReleased,
    innerStepStatusColorForReleaseStepStatus,
    isAppReleased,
    isAppSubmitted,
} from '../../helpers';
import {
    AmazonAppStoreData,
    AmazonAppStoreState,
    AppleAppStoreData,
    AppleAppStoreDefaultSettings,
    AppleAppStoreState,
    AppPlatform,
    AppStoreReviewSubmissionItemState,
    AppStoreReviewSubmissionItemType,
    AppStoreReviewSubmissionState,
    AppStoreState,
    AppStoreType,
    AutomationType,
    GoogleAppStoreSettings,
    GooglePlayReleaseLifecycleState,
    GooglePlayStoreData,
    GooglePlayStoreState,
    HuaweiAppGallerySettings,
    HuaweiAppStoreData,
    HuaweiAppStoreState,
    IntegrationType,
    ReleaseStepStatus,
    ReleaseStepStoreReview,
    ReleaseStepStoreReviewData,
    ReviewSubmissionItem,
    RunwayResponseType,
    SamsungAppStoreData,
    SamsungAppStoreState,
} from '../../types';
import { runwayColors } from '../styles/globalStyles';
import { displayConsoleNameForAppStoreType } from '../../helpers/displayHelpers';
import { ReleaseUpdatable } from '../../actions/updatable';
import { BaseUpdateReleaseRequest, tryUpdateRelease } from '../../actions/release';
import {
    selectAppPlatform,
    selectAppStoreType,
    selectIsReleaseRollback,
    selectOrg,
    selectRelease,
    selectReleasedAt,
    selectReleaseStep,
    selectStepRun,
} from '../../selectors/selectors';
import { useDispatch, useSelector } from 'react-redux';
import Layout, { Content, Footer } from 'antd/lib/layout/layout';
import styles from '@/components/styles/stepStyles';
import { EmptyStateWrapper } from '@/components/releaseEmptyState/EmptyStateWrapper';
import Space from 'antd/lib/space';
import Text from 'antd/lib/typography/Text';
import { ReleaseEventsTimelineSection } from '@/components/releaseOverview/ReleaseTimelineSection';
import { ReleaseTimelineSectionContext } from '@/components/timelineEvents/EventsTimeline';
import TrackedButton from '@/helpers/TrackedButton';
import * as AnalyticEvents from '@/utils/analytics';
import { ArrowRightOutlined, CloseOutlined, InfoCircleOutlined } from '@ant-design/icons';
import Popconfirm from 'antd/lib/popconfirm';
import PermissionedActionButton from '@/components/HOCs/PermissionedActionButton';
import { RunwayUserAction } from '@/helpers/aclHelpers';
import Tooltip from 'antd/lib/tooltip';
import { globalMessage } from '@/utils/globalMessage';
import { AppStoreSubmissionCollapse } from '@/components/AppStoreItems/AppStoreSubmissionCollapse';
import EditAppleAppStoreReleaseSettings from '@/components/EditAppleAppStoreReleaseSettings';
import EditGoogleReleaseSettingsContainer from '@/components/appSettings/EditGoogleReleaseSettingsContainer';
import { BuildAndReviewSettingsCard } from '@/components/AppStoreItems/BuildAndReviewSettingsCard';
import { renderReviewsSubmissionItemCard } from '@/components/StepContentSubmission/helpers';
import EditHuaweiReleaseSettingsContainer from '@/components/appSettings/EditHuaweiReleaseSettingsContainer';
import { useCallUpdatable, useError, useIsLoading, useUpdated } from '@/utils/hooks/helpers';
import { ReleaseStepOrStepRunComponentProps } from '@/components/ValidatedRouteComponents';
import { StepRunUpdatable } from '@/actions/stepRun';

export const StepContentAppReview: React.FC<ReleaseStepOrStepRunComponentProps> = ({
    orgId,
    appId,
    releaseId,
    releaseStepType,
    stepRunComponentProps,
}) => {
    const platform = useSelector((state: RootState) => selectAppPlatform(state, appId));
    const release = useSelector((state: RootState) => selectRelease(state, releaseId));
    const isRollback = useSelector((state: RootState) => selectIsReleaseRollback(state, releaseId));
    const enablePlayReleaseLifecycle = useSelector(
        (state: RootState) => !!selectOrg(state, orgId)?.enablePlayReleaseLifecycle,
    );
    const releaseStep = useSelector((state: RootState) =>
        selectReleaseStep(state, releaseId, releaseStepType),
    ) as ReleaseStepStoreReview;

    const displayableVersion = release.displayableVersion;

    const stepRunId = stepRunComponentProps?.stepRunId;
    const stepRun = useSelector((state: RootState) => selectStepRun(state, stepRunId));
    const stepData: ReleaseStepStoreReviewData =
        stepRun ? { ...stepRun.data.summaryData, ...stepRun.data.complementaryData } : releaseStep.data;
    const appStoreType = useSelector((state: RootState) => {
        return stepRun ? stepData.appStoreType : selectAppStoreType(state, appId);
    });
    const automationRuns = stepRun ? stepRun.automationRuns : releaseStep.automationRuns;

    const appStoreReleaseSettings = stepData.appStoreReleaseSettings;

    const googleAppStoreState = (stepData.appStoreData as GooglePlayStoreData)?.state;
    const amazonAppStoreState = (stepData.appStoreData as AmazonAppStoreData)?.state;
    const huaweiAppStoreState = (stepData.appStoreData as HuaweiAppStoreData)?.state;
    const samsungAppStoreState = (stepData.appStoreData as SamsungAppStoreData)?.state;

    const appleAppStoreData = stepData.appStoreData as AppleAppStoreData;
    const appleAppStoreState = appleAppStoreData?.versionReviewSubmissionItem?.state;

    const autoStagedRolloutAutomationId = getAutomationIdByType(automationRuns, AutomationType.AutoStagedRollout);

    let appStoreState: AppStoreState;
    if (appleAppStoreState) {
        appStoreState = appleAppStoreState;
    } else if (googleAppStoreState) {
        appStoreState = googleAppStoreState;
    } else if (amazonAppStoreState) {
        appStoreState = amazonAppStoreState;
    } else if (huaweiAppStoreState) {
        appStoreState = huaweiAppStoreState;
    } else if (samsungAppStoreState) {
        appStoreState = samsungAppStoreState;
    }

    const googleStoreData = stepData.appStoreData as GooglePlayStoreData;
    const huaweiAppStoreData = stepData.appStoreData as HuaweiAppStoreData;
    const samsungAppStoreData = stepData.appStoreData as SamsungAppStoreData;

    const isReleased = isAppReleased(
        appStoreType,
        appleAppStoreState,
        googleAppStoreState,
        amazonAppStoreState,
        huaweiAppStoreState,
        samsungAppStoreState,
    );
    const isAppleAppStore = getIsAppleAppStore(appStoreType);
    const isGooglePlayStore = getIsGooglePlayStore(appStoreType);
    const isHuaweiAppGallery = getIsHuaweiAppGallery(appStoreType);
    const appCanBeDeveloperRejected = appleAppStoreState ? appleAppCanBeDeveloperRejected(appleAppStoreState) : false;
    const isSubmitted = isAppSubmitted(
        appStoreType,
        appleAppStoreState,
        appleAppStoreData?.relevantReviewSubmission?.state,
        googleAppStoreState,
        amazonAppStoreState,
        huaweiAppStoreState,
        samsungAppStoreState,
    );
    const markedRejectedAt = stepData.markedRejectedAt;

    // An already-live release with no lifecycle data (pre-flag or superseded) has no review
    // state to read, so fall back to the legacy copy instead of showing "in review".
    const hasPlayReviewLifecycle =
        enablePlayReleaseLifecycle && !(isReleased && !googleStoreData?.releaseLifecycleState);

    const storeUrl = stepData.storeUrl;
    const stepStatus = stepRun ? stepRun.status : releaseStep.status;

    const [
        rejectAppleSubmissionStepRun,
        rejectAppleSubmissionIsLoadingStepRun,
        rejectAppleSubmissionResponseTypeStepRun,
        rejectAppleSubmissionErrorStepRun,
    ] = useCallUpdatable(StepRunUpdatable.RejectAppleSubmission, stepRunComponentProps);

    const rejectAppleSubmissionIsLoadingReleaseStep = useIsLoading(ReleaseUpdatable.RejectAppleSubmission, releaseId);
    const rejectAppleSubmissionSuccessReleaseStep = useUpdated(ReleaseUpdatable.RejectAppleSubmission, releaseId);
    const rejectAppleSubmissionErrorReleaseStep = useError(ReleaseUpdatable.RejectAppleSubmission, releaseId);

    const rejectAppleSubmissionIsLoading =
        stepRunComponentProps ? rejectAppleSubmissionIsLoadingStepRun : rejectAppleSubmissionIsLoadingReleaseStep;
    const rejectAppleSubmissionSuccess =
        stepRunComponentProps ?
            rejectAppleSubmissionResponseTypeStepRun === RunwayResponseType.Success
        :   rejectAppleSubmissionSuccessReleaseStep;
    const rejectAppleSubmissionError =
        stepRunComponentProps ? rejectAppleSubmissionErrorStepRun : rejectAppleSubmissionErrorReleaseStep;

    const [
        markAndroidSubmissionRejectedStepRun,
        markAndroidSubmissionRejectedIsLoadingStepRun,
        ,
        markAndroidSubmissionRejectedErrorStepRun,
    ] = useCallUpdatable(StepRunUpdatable.MarkAndroidSubmissionRejected, stepRunComponentProps);

    const markAndroidSubmissionRejectedIsLoadingReleaseStep = useIsLoading(
        ReleaseUpdatable.MarkReleaseRejected,
        releaseId,
    );
    const markAndroidSubmissionRejectedErrorReleaseStep = useError(ReleaseUpdatable.MarkReleaseRejected, releaseId);

    const markAndroidSubmissionRejectedIsLoading =
        stepRunComponentProps ?
            markAndroidSubmissionRejectedIsLoadingStepRun
        :   markAndroidSubmissionRejectedIsLoadingReleaseStep;

    const markAndroidSubmissionRejectedError =
        stepRunComponentProps ?
            markAndroidSubmissionRejectedErrorStepRun
        :   markAndroidSubmissionRejectedErrorReleaseStep;

    const [editReleaseSettingsModalVisible, setEditReleaseSettingsModalVisible] = React.useState(false);

    React.useEffect(() => {
        if (rejectAppleSubmissionSuccess) {
            globalMessage.success('Successfully rejected your submission.');
        }
    }, [rejectAppleSubmissionSuccess]);

    React.useEffect(() => {
        if (rejectAppleSubmissionError) {
            globalMessage.error(rejectAppleSubmissionError);
        }
    }, [rejectAppleSubmissionError]);

    React.useEffect(() => {
        if (markAndroidSubmissionRejectedError) {
            globalMessage.error(markAndroidSubmissionRejectedError);
        }
    }, [markAndroidSubmissionRejectedError]);

    const dispatch = useDispatch();

    const rejectAppleSubmission = () => {
        if (stepRunComponentProps) {
            rejectAppleSubmissionStepRun();
        } else {
            const request: BaseUpdateReleaseRequest = {
                orgId,
                appId,
                releaseId,
            };
            // Reject the submission
            dispatch(tryUpdateRelease(ReleaseUpdatable.RejectAppleSubmission, request, request.releaseId));
        }
    };

    const rejectAndroidSubmission = () => {
        const request: BaseUpdateReleaseRequest = {
            orgId,
            appId,
            releaseId,
        };
        // Reject the submission
        if (stepRunComponentProps) {
            markAndroidSubmissionRejectedStepRun();
        } else {
            dispatch(tryUpdateRelease(ReleaseUpdatable.MarkReleaseRejected, request, request.releaseId));
        }
    };

    const doesRelevantReviewSubmissionHasUnresolvedIssues =
        appleAppStoreData?.relevantReviewSubmission?.state === AppStoreReviewSubmissionState.UnresolvedIssues;

    const filteredReviewSubmissionItems = appleAppStoreData?.relevantReviewSubmission?.reviewSubmissionItems?.filter(
        (item) => item.state !== AppStoreReviewSubmissionItemState.Removed,
    );

    const isReleaseReleased = useSelector((state: RootState) => !!selectReleasedAt(state, releaseId));

    const hasReviewSubmission = !!appleAppStoreData?.relevantReviewSubmission;

    const shouldHideReviewSubmissionInterfaces = isReleaseReleased && !hasReviewSubmission; // Hide the review submission interfaces if the release is released and there is no review submission data.

    return (
        <Layout>
            <Content
                style={{
                    ...styles.innerContent,
                    ...styles.contentWithFooterStyle,
                    paddingTop: 0,
                }}
            >
                <div style={{ display: 'flex', marginBottom: 30, justifyContent: 'space-between', gap: 12 }}>
                    <EmptyStateWrapper
                        releaseStepType={releaseStepType}
                        appId={appId}
                        releaseId={releaseId}
                        featureSections={[]}
                        shouldForceDisable
                    >
                        {(isReleaseFromScraper) => {
                            return (
                                <Space
                                    direction="vertical"
                                    size={4}
                                    style={{
                                        flex: 1,
                                        paddingTop: 28,
                                    }}
                                >
                                    <Text
                                        style={{
                                            fontSize: 30,
                                            color: statusColorForReleaseStepStatus(stepStatus, isReleaseFromScraper),
                                            fontWeight: 600,
                                        }}
                                    >
                                        {titleForState(
                                            platform,
                                            appStoreType,
                                            stepStatus,
                                            appStoreState,
                                            isReleaseFromScraper,
                                            markedRejectedAt,
                                            hasPlayReviewLifecycle,
                                            googleStoreData?.releaseLifecycleState,
                                        )}
                                    </Text>
                                    <Text type="secondary">
                                        {descriptionForState(
                                            platform,
                                            appStoreType,
                                            stepStatus,
                                            appStoreState,
                                            isReleaseFromScraper,
                                            markedRejectedAt,
                                            hasPlayReviewLifecycle,
                                            googleStoreData?.releaseLifecycleState,
                                        )}
                                    </Text>
                                </Space>
                            );
                        }}
                    </EmptyStateWrapper>
                    <ReleaseEventsTimelineSection
                        orgId={orgId}
                        appId={appId}
                        stepRunId={stepRunComponentProps?.stepRunId}
                        releaseId={releaseId}
                        context={ReleaseTimelineSectionContext.ReleaseStep}
                        releaseStepType={releaseStepType}
                    />
                </div>
                {isSubmitted && isGooglePlayStore && (
                    <div>
                        <BuildAndReviewSettingsCard
                            stepRunComponentProps={stepRunComponentProps}
                            orgId={orgId}
                            appId={appId}
                            releaseId={releaseId}
                            releaseStepType={releaseStepType}
                            googleStoreData={googleStoreData}
                            huaweiStoreData={huaweiAppStoreData}
                            samsungStoreData={samsungAppStoreData}
                            appStoreType={appStoreType}
                            platform={platform}
                            autoStagedRolloutAutomationId={autoStagedRolloutAutomationId}
                            appStoreReleaseSettings={appStoreReleaseSettings}
                            isRollback={isRollback}
                            onShowAppStoreReleaseSettings={() => setEditReleaseSettingsModalVisible(true)}
                            storeUrl={storeUrl}
                            isReleased={isReleased}
                            lastUploadedBuild={stepData.lastSuccessfullyUploadedBuild}
                            {...stepData}
                            appStoreData={appleAppStoreData}
                        />
                    </div>
                )}
                {isSubmitted && isAppleAppStore && (
                    <>
                        {shouldHideReviewSubmissionInterfaces ?
                            <div>
                                <BuildAndReviewSettingsCard
                                    stepRunComponentProps={stepRunComponentProps}
                                    orgId={orgId}
                                    appId={appId}
                                    releaseId={releaseId}
                                    releaseStepType={releaseStepType}
                                    googleStoreData={null}
                                    huaweiStoreData={null}
                                    samsungStoreData={null}
                                    appStoreType={appStoreType}
                                    platform={platform}
                                    autoStagedRolloutAutomationId={autoStagedRolloutAutomationId}
                                    appStoreReleaseSettings={appStoreReleaseSettings}
                                    isRollback={isRollback}
                                    onShowAppStoreReleaseSettings={() => setEditReleaseSettingsModalVisible(true)}
                                    storeUrl={storeUrl}
                                    isReleased={isReleased}
                                    lastUploadedBuild={stepData.lastSuccessfullyUploadedBuild}
                                    {...stepData}
                                    appStoreData={appleAppStoreData}
                                />
                            </div>
                        :   <div>
                                <AppStoreSubmissionCollapse
                                    reviewSubmission={appleAppStoreData?.relevantReviewSubmission}
                                    integrationId={IntegrationType.Apple}
                                    selectedBuilds={null}
                                    isAppVersionInsideReviewSubmission={true}
                                >
                                    {filteredReviewSubmissionItems?.map((reviewSubmissionItem) => {
                                        switch (reviewSubmissionItem.type) {
                                            case AppStoreReviewSubmissionItemType.AppBuildVersion:
                                                return (
                                                    <BuildAndReviewSettingsCard
                                                        stepRunComponentProps={stepRunComponentProps}
                                                        key={reviewSubmissionItem.id}
                                                        orgId={orgId}
                                                        appId={appId}
                                                        releaseId={releaseId}
                                                        releaseStepType={releaseStepType}
                                                        googleStoreData={null}
                                                        huaweiStoreData={null}
                                                        samsungStoreData={null}
                                                        appStoreType={appStoreType}
                                                        autoStagedRolloutAutomationId={autoStagedRolloutAutomationId}
                                                        platform={platform}
                                                        appStoreReleaseSettings={appStoreReleaseSettings}
                                                        isRollback={isRollback}
                                                        onShowAppStoreReleaseSettings={() =>
                                                            setEditReleaseSettingsModalVisible(true)
                                                        }
                                                        storeUrl={storeUrl}
                                                        isReleased={isReleased}
                                                        state={stateForItem(reviewSubmissionItem, appleAppStoreState)}
                                                        lastUploadedBuild={stepData.lastSuccessfullyUploadedBuild}
                                                        {...stepData}
                                                        appStoreData={appleAppStoreData}
                                                    />
                                                );
                                            default: {
                                                const shouldHaveRemoveButtonEnabled =
                                                    doesRelevantReviewSubmissionHasUnresolvedIssues &&
                                                    reviewSubmissionItem.state ===
                                                        AppStoreReviewSubmissionItemState.Rejected;

                                                return renderReviewsSubmissionItemCard({
                                                    reviewSubmissionItemId: reviewSubmissionItem.id,
                                                    item: reviewSubmissionItem.item,
                                                    type: reviewSubmissionItem.type,
                                                    state: stateForItem(reviewSubmissionItem, appleAppStoreState),
                                                    orgId,
                                                    appId,
                                                    releaseId,
                                                    removeButtonText: `Remove from ${displayableVersion} submission`,
                                                    isRemoveButtonDisabled: !shouldHaveRemoveButtonEnabled,
                                                    stepRunComponentProps,
                                                });
                                            }
                                        }
                                    })}
                                </AppStoreSubmissionCollapse>
                            </div>
                        }
                    </>
                )}
                {isSubmitted && isHuaweiAppGallery && (
                    <div>
                        <BuildAndReviewSettingsCard
                            stepRunComponentProps={stepRunComponentProps}
                            orgId={orgId}
                            appId={appId}
                            releaseId={releaseId}
                            releaseStepType={releaseStepType}
                            googleStoreData={googleStoreData}
                            huaweiStoreData={huaweiAppStoreData}
                            samsungStoreData={samsungAppStoreData}
                            appStoreType={appStoreType}
                            platform={platform}
                            autoStagedRolloutAutomationId={autoStagedRolloutAutomationId}
                            appStoreReleaseSettings={appStoreReleaseSettings}
                            isRollback={isRollback}
                            onShowAppStoreReleaseSettings={() => setEditReleaseSettingsModalVisible(true)}
                            storeUrl={storeUrl}
                            isReleased={isReleased}
                            lastUploadedBuild={stepData.lastSuccessfullyUploadedBuild}
                            {...stepData}
                            appStoreData={appleAppStoreData}
                        />
                    </div>
                )}
            </Content>
            <Footer style={styles.footerStyle}>
                <Space>
                    <TrackedButton
                        eventName={AnalyticEvents.Event.ClickAppStoreConsoleButton}
                        orgId={orgId}
                        appId={appId}
                        releaseId={releaseId}
                        releaseStepType={releaseStepType}
                        context={AnalyticEvents.Context.ReleaseStep}
                        href={storeUrl}
                        icon={<ArrowRightOutlined style={{ color: runwayColors.info }} />}
                    >
                        {buttonTextForState(appStoreType)}
                    </TrackedButton>
                    {isAppleAppStore && (
                        <Popconfirm
                            title="Are you sure you want to reject this build?"
                            okText="Yes, reject"
                            okButtonProps={{ danger: true }}
                            onConfirm={rejectAppleSubmission}
                        >
                            <PermissionedActionButton
                                releaseId={releaseId}
                                actionType={RunwayUserAction.UADevRejectAppUpdate}
                                icon={<CloseOutlined style={{ color: runwayColors.statusRed }} />}
                                loading={rejectAppleSubmissionIsLoading}
                                disabled={!isSubmitted || isReleased || !appCanBeDeveloperRejected}
                            >
                                Pull from review
                            </PermissionedActionButton>
                        </Popconfirm>
                    )}
                    {isGooglePlayStore && (
                        <Space>
                            <Popconfirm
                                title={
                                    <div style={{ width: 300 }}>
                                        <Text>
                                            Marking this update as developer rejected by Google will revoke release
                                            completion in Runway and enable upload of a new build for this version. Are
                                            Are you sure you want to proceed?
                                        </Text>
                                    </div>
                                }
                                okText="Yes, mark as rejected by Google"
                                onConfirm={rejectAndroidSubmission}
                                style={{ width: 300 }}
                            >
                                <PermissionedActionButton
                                    releaseId={releaseId}
                                    actionType={RunwayUserAction.UADevRejectAppUpdate}
                                    icon={<CloseOutlined style={{ color: runwayColors.statusRed }} />}
                                    loading={markAndroidSubmissionRejectedIsLoading}
                                    disabled={!isSubmitted || !!markedRejectedAt}
                                >
                                    Mark submission rejected by Google
                                </PermissionedActionButton>
                            </Popconfirm>
                            <Tooltip title="Let Runway know if your release was rejected by Google review – this will change the status of the release back to active and enable selection of a new build. ">
                                <InfoCircleOutlined style={{ color: runwayColors.primary }} />
                            </Tooltip>
                        </Space>
                    )}
                </Space>
            </Footer>
            {isAppleAppStore && (
                <>
                    <EditAppleAppStoreReleaseSettings
                        stepRunComponentProps={stepRunComponentProps}
                        orgId={orgId}
                        appId={appId}
                        releaseId={releaseId}
                        displayableVersion={displayableVersion}
                        open={editReleaseSettingsModalVisible}
                        appStoreReleaseSettings={appStoreReleaseSettings as AppleAppStoreDefaultSettings}
                        appStoreData={appleAppStoreData}
                        isRollback={isRollback}
                        onClose={() => setEditReleaseSettingsModalVisible(false)}
                        onSuccess={() => setEditReleaseSettingsModalVisible(false)}
                    />
                </>
            )}
            {isGooglePlayStore && (
                <>
                    <EditGoogleReleaseSettingsContainer
                        stepRunComponentProps={stepRunComponentProps}
                        orgId={orgId}
                        appId={appId}
                        releaseId={releaseId}
                        autoStagedRolloutAutomationId={autoStagedRolloutAutomationId}
                        isReleased={isReleased}
                        open={editReleaseSettingsModalVisible}
                        releaseSettings={appStoreReleaseSettings as GoogleAppStoreSettings}
                        onClose={() => setEditReleaseSettingsModalVisible(false)}
                        onSuccess={() => setEditReleaseSettingsModalVisible(false)}
                    />
                </>
            )}
            {isHuaweiAppGallery && (
                <>
                    <EditHuaweiReleaseSettingsContainer
                        stepRunComponentProps={stepRunComponentProps}
                        orgId={orgId}
                        appId={appId}
                        releaseId={releaseId}
                        autoStagedRolloutAutomationId={autoStagedRolloutAutomationId}
                        open={editReleaseSettingsModalVisible}
                        releaseSettings={appStoreReleaseSettings as HuaweiAppGallerySettings}
                        onClose={() => setEditReleaseSettingsModalVisible(false)}
                        onSuccess={() => setEditReleaseSettingsModalVisible(false)}
                    />
                </>
            )}
        </Layout>
    );
};

export const titleForState = (
    platform: AppPlatform,
    appStoreType: AppStoreType,
    status: ReleaseStepStatus,
    appStoreState: AppStoreState,
    isReleaseFromScraper: boolean,
    markedRejectedAt?: string,
    enablePlayReleaseLifecycle?: boolean,
    googleLifecycleState?: GooglePlayReleaseLifecycleState | '',
) => {
    const isAppleAppStore = getIsAppleAppStore(appStoreType);
    const isGooglePlayWithLifecycle = appStoreType === AppStoreType.Google && enablePlayReleaseLifecycle;

    // Google Play behaves like iOS once the org has the lifecycle signal: the Review
    // step's title is "Approved" when the release passes Google review (analog of
    // Apple's READY_FOR_SALE), "Rejected" when Google rejects (analog of Apple's
    // MetadataRejected/Rejected), and an in-review string otherwise. Without lifecycle,
    // Google has no notion of review pass/fail so the title stays as the legacy
    // "Released" / "Submitted for review" copy.
    const isSamsungGalaxyStore = getIsSamsungGalaxyStore(appStoreType);
    const approvedText = isAppleAppStore || isGooglePlayWithLifecycle || isSamsungGalaxyStore ? 'Approved' : 'Released';

    if (isReleaseFromScraper) {
        return approvedText;
    }

    if (isGooglePlayWithLifecycle) {
        if (markedRejectedAt) {
            return 'Rejected by Google';
        }
        switch (googleLifecycleState) {
            case GooglePlayReleaseLifecycleState.ApprovedNotPublished:
            case GooglePlayReleaseLifecycleState.Published:
                return 'Approved';
            case GooglePlayReleaseLifecycleState.NotApproved:
                return 'Rejected';
            case GooglePlayReleaseLifecycleState.InReview:
                return 'In review';
            case GooglePlayReleaseLifecycleState.NotSentForReview:
                return 'Waiting for review';
            default:
                // Draft / Unspecified / empty: post-submit, pre-refresh transient state.
                return status === ReleaseStepStatus.Inactive || status === ReleaseStepStatus.Skipped ?
                        'Nothing to review'
                    :   'In review';
        }
    }

    const appStoreTitle = appStoreTitleForState(appStoreType, appStoreState, markedRejectedAt);

    switch (status) {
        case ReleaseStepStatus.Inactive:
        case ReleaseStepStatus.Skipped:
            return appStoreTitle || 'Nothing to review';
        case ReleaseStepStatus.Blocked:
        case ReleaseStepStatus.Pending:
            return appStoreTitle;
        case ReleaseStepStatus.PendingChecklistOnly:
        case ReleaseStepStatus.Ready:
            return approvedText;
    }
};

const descriptionForState = (
    platform: AppPlatform,
    appStoreType: AppStoreType,
    status: ReleaseStepStatus,
    appStoreState: AppStoreState,
    isReleaseFromScraper: boolean,
    markedRejectedAt?: string,
    enablePlayReleaseLifecycle?: boolean,
    googleLifecycleState?: GooglePlayReleaseLifecycleState | '',
) => {
    let approvedText: string;
    switch (appStoreType) {
        case AppStoreType.Apple:
            approvedText = 'Your update has been approved by Apple.';
            break;
        case AppStoreType.Google:
            // For orgs with the Play release-lifecycle signal (RUN-15515), we now have an
            // explicit review/approval state and can show the same fidelity as iOS. Gate on
            // the org flag, not on lifecycle-data presence — data is empty in the post-submit
            // / pre-refresh window, so a data-gate flips flagged orgs back to legacy copy.
            if (enablePlayReleaseLifecycle) {
                if (
                    googleLifecycleState === GooglePlayReleaseLifecycleState.Published ||
                    googleLifecycleState === GooglePlayReleaseLifecycleState.ApprovedNotPublished
                ) {
                    approvedText = 'Your update has been approved by Google Play.';
                } else if (googleLifecycleState === GooglePlayReleaseLifecycleState.NotApproved) {
                    approvedText = 'Your update was not approved by Google Play. Release is blocked.';
                } else if (googleLifecycleState === GooglePlayReleaseLifecycleState.InReview) {
                    approvedText = 'Your update is in review by Google Play.';
                } else {
                    // Draft / NotSentForReview / Unspecified / empty: pre-submit, or transient
                    // post-submit / pre-refresh window where review hasn't started yet.
                    approvedText = 'Your update has not yet been submitted for review by Google Play.';
                }
            } else {
                approvedText = `Your update has been submitted, and may still be in review with Google. Runway is unable to determine review status due to a current limitation of the Google Play API. Visit the Play Console to see review status.`;
            }
            break;
        case AppStoreType.Amazon:
            approvedText = `Your update has been submitted, and may still be in review with Amazon. Runway is unable to determine review status due to a current limitation of the Amazon Appstore API. Visit the Appstore Console to see review status.`;
            break;
        case AppStoreType.Huawei:
            approvedText = 'Your app update has been approved by Huawei.';
            break;
        case AppStoreType.Samsung:
            approvedText = 'Your app update has been approved by Samsung.';
            break;
    }

    if (isReleaseFromScraper) {
        return approvedText;
    }

    switch (status) {
        case ReleaseStepStatus.Inactive:
        case ReleaseStepStatus.Skipped:
            switch (appStoreType) {
                case AppStoreType.Apple:
                    return "Your update will be reviewed by Apple once it's submitted.";
                case AppStoreType.Google:
                    return "Your update will be reviewed by Google once it's submitted.";
                case AppStoreType.Amazon:
                    return "Your update will be reviewed by Amazon once it's submitted.";
                case AppStoreType.Huawei:
                    return "Your update will be reviewed by Huawei once it's submitted.";
                case AppStoreType.Samsung:
                    return "Your update will be reviewed by Samsung once it's submitted.";
                default:
                    return '<MISSING>';
            }
        case ReleaseStepStatus.Blocked:
        case ReleaseStepStatus.Pending: {
            switch (appStoreType) {
                case AppStoreType.Apple:
                    return appleAppStoreDescriptionForAppStoreState(appStoreState as AppleAppStoreState);
                case AppStoreType.Google:
                    return markedRejectedAt ?
                            'Your update was rejected by Google review. Select a new build and re-submit for review'
                        :   googleAppStoreDescriptionForAppStoreState(
                                appStoreState as GooglePlayStoreState,
                                enablePlayReleaseLifecycle,
                                googleLifecycleState,
                            );
                case AppStoreType.Amazon:
                    return amazonAppStoreDescriptionForAppStoreState(appStoreState as AmazonAppStoreState);
                case AppStoreType.Huawei:
                    return huaweiAppStoreDescriptionForAppStoreState(appStoreState as HuaweiAppStoreState);
                case AppStoreType.Samsung:
                    return samsungAppStoreDescriptionForAppStoreState(appStoreState as SamsungAppStoreState);
                default:
                    return '<MISSING>';
            }
        }
        case ReleaseStepStatus.PendingChecklistOnly:
        case ReleaseStepStatus.Ready:
            return approvedText;
    }
};

const buttonTextForState = (appStoreType: AppStoreType) => {
    return displayConsoleNameForAppStoreType(appStoreType);
};

const statusColorForReleaseStepStatus: (stepStatus: ReleaseStepStatus, isReleaseFromScraper: boolean) => string = (
    stepStatus,
    isReleaseFromScraper,
) => {
    if (isReleaseFromScraper) {
        return runwayColors.statusGrey;
    }

    return innerStepStatusColorForReleaseStepStatus(stepStatus);
};

const appleAppStoreDescriptionForAppStoreState = (state: AppleAppStoreState) => {
    const isApprovedOrReleased = appleAppIsApprovedOrReleased(state);

    if (isApprovedOrReleased) {
        return 'Your update has been approved by Apple.';
    } else {
        switch (state) {
            case AppleAppStoreState.WaitingForReview:
                return 'Your update is in the queue to be reviewed by Apple.';
            case AppleAppStoreState.WaitingForExportCompliance:
                return 'Your update is waiting for export compliance review by Apple.';
            case AppleAppStoreState.InReview:
                return 'Your update is under review by Apple.';
            case AppleAppStoreState.Rejected:
            case AppleAppStoreState.Accepted:
                return 'One or more of your review submission items has unresolved issues. Please resolve issues with the rejected items or remove them from your submission to proceed.';
            case AppleAppStoreState.MetadataRejected:
                return "Your update's metadata has been rejected. Go to App Store Connect for more information.";
            default:
                return '';
        }
    }
};

const samsungAppStoreDescriptionForAppStoreState = (state: SamsungAppStoreState) => {
    switch (state) {
        case SamsungAppStoreState.Registering:
            return `Your app update is registering.`;
        case SamsungAppStoreState.ForSale:
            return `Your app update is for sale.`;
        case SamsungAppStoreState.ReadyForChange:
            return `Your app update is ready for change.`;
        case SamsungAppStoreState.ReadyToPreExamination:
            return `Your app update is ready for pre-examination.`;
        case SamsungAppStoreState.UnderContentReview:
            return `Your app update is under content review.`;
        case SamsungAppStoreState.ReadyForReview:
        case SamsungAppStoreState.ReadyToPreReviews:
        case SamsungAppStoreState.UnderPreReviews:
        case SamsungAppStoreState.ReadyForContentReview:
        case SamsungAppStoreState.ReadyForDeviceTest:
        case SamsungAppStoreState.UnderDeviceTest:
        case SamsungAppStoreState.ReadyForTestConfirmation:
        case SamsungAppStoreState.UnderTestConfirmation:
            return `Your app update has been submitted and is in review.`;
        case SamsungAppStoreState.Canceled:
            return `Your app update has been canceled.`;
        default:
            return `Your update will be reviewed by Samsung once it's submitted.`;
    }
};

const huaweiAppStoreDescriptionForAppStoreState = (state: HuaweiAppStoreState) => {
    switch (state) {
        case HuaweiAppStoreState.Released:
            return `Your app update has been approved.`;
        case HuaweiAppStoreState.Reviewing:
            return `Your app update is in review.`;
        case HuaweiAppStoreState.ReleaseCanceled:
            return 'Your release has been canceled';
        case HuaweiAppStoreState.ReleaseRejected:
        case HuaweiAppStoreState.RemovalRejected:
        case HuaweiAppStoreState.Removed:
        case HuaweiAppStoreState.UpdateRejected:
            return 'Your app update has unresolved issues. Please review the release in Huawei AppGallery Connect for more information.';
        case HuaweiAppStoreState.Releasing:
            return 'Your app update is releasing.';
        case HuaweiAppStoreState.Updating:
            return 'Your app update is updating.';
        default:
            return `Your update will be reviewed by Huawei once it's submitted.`;
    }
};

const amazonAppStoreDescriptionForAppStoreState = (state: AmazonAppStoreState) => {
    switch (state) {
        case AmazonAppStoreState.Completed:
        case AmazonAppStoreState.Live:
            return `Your app update has been approved.`;
        case AmazonAppStoreState.Review:
            return 'Your app has been submitted and is currently in review by Amazon.';
        case AmazonAppStoreState.Submitted:
            return 'Your update has been submitted, and is waiting for review by Amazon.';
        default:
            return `Your update will be reviewed by Amazon once it's submitted.`;
    }
};

const googleAppStoreDescriptionForAppStoreState = (
    state: GooglePlayStoreState,
    enablePlayReleaseLifecycle?: boolean,
    lifecycleState?: GooglePlayReleaseLifecycleState | '',
) => {
    // Lifecycle-aware copy for orgs with the Play release-lifecycle signal (RUN-15515).
    // Gates on the org flag rather than lifecycle-data presence so flagged orgs don't
    // flip back to the legacy "Runway is unable to determine review status" copy in the
    // post-submit / pre-refresh window when lifecycle data is still empty.
    if (enablePlayReleaseLifecycle) {
        switch (lifecycleState) {
            case GooglePlayReleaseLifecycleState.ApprovedNotPublished:
            case GooglePlayReleaseLifecycleState.Published:
                return 'Your update has been approved by Google Play.';
            case GooglePlayReleaseLifecycleState.NotApproved:
                return 'Your update was not approved by Google Play. Release is blocked.';
            default:
                // InReview / NotSentForReview / Draft / Unspecified / empty.
                return 'Your update is in review by Google Play.';
        }
    }

    const isReleased = googleAppIsReleased(state);
    if (isReleased) {
        return `Your update has been submitted, and may still be in review with Google. Runway is unable to determine review status due to a current limitation of the Google Play API. Visit the Play Console to see review status.`;
    } else if (state === GooglePlayStoreState.Draft) {
        return "Your update will be reviewed by Google once it's submitted.";
    } else {
        return 'Go to the Google Play Console to check your review status.';
    }
};

const stateForItem = (item: ReviewSubmissionItem, appStoreState: AppStoreState): AppStoreReviewSubmissionItemState => {
    if (
        item.state === AppStoreReviewSubmissionItemState.ReadyForReview &&
        appStoreState === AppleAppStoreState.WaitingForReview
    ) {
        return null;
    } else if (
        item.state === AppStoreReviewSubmissionItemState.ReadyForReview &&
        appStoreState === AppleAppStoreState.InReview
    ) {
        return AppStoreReviewSubmissionItemState.InReview;
    }
    return item.state;
};
